import {
    Button,
    Box,
    Card,
    CardContent,
    TextField,
    CircularProgress
} from '@mui/material';
import { UnauthenticatedTemplate } from '@azure/msal-react';
import { useConfiguration } from '@/contexts/configuration/ConfigurationContext';
import { useEffect, useState } from 'react';
import { useConsentStore } from '@/stores/consentStore';
import { LoginType } from '@/models/company';
import { useGetLoginType } from '@/services/usersQuery';

const UnauthenticatedContent = () => {
    const { loginAD, loginB2C } = useConfiguration();
    const [email, setEmail] = useState('');
    const [triggerEmail, setTriggerEmail] = useState<string | null>(null);
    const { clearAllPostponed } = useConsentStore();

    useEffect(() => {
        clearAllPostponed();
    }, [clearAllPostponed]);

    const { data, isFetching, isError } = useGetLoginType(
        triggerEmail ?? '',
        !!triggerEmail
    );

    useEffect(() => {
        if (!triggerEmail || isFetching) return;

        if (isError || !data) {
            // Fall back to B2C on network error so login remains possible.
            loginB2C(triggerEmail);
        } else if (data.loginType === LoginType.AzureAD) {
            loginAD(triggerEmail);
        } else {
            loginB2C(triggerEmail);
        }
        setTriggerEmail(null);
    }, [triggerEmail, isFetching, isError, data, loginAD, loginB2C]);

    const handleContinue = () => {
        const trimmedEmail = email.trim();
        if (!trimmedEmail) return;
        setTriggerEmail(trimmedEmail);
    };

    return (
        <Box sx={{ maxWidth: '30rem', margin: 'auto' }}>
            <Card sx={{ textAlign: 'center' }}>
                <CardContent>
                    Please login to access the InstaPaid backoffice.
                    <TextField
                        sx={{ marginTop: '1rem' }}
                        label="Email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleContinue()}
                        fullWidth
                        autoComplete="email"
                        disabled={isFetching}
                    />
                    <Button
                        sx={{ marginTop: '1rem' }}
                        variant="contained"
                        fullWidth
                        onClick={handleContinue}
                        disabled={isFetching || !email.trim()}
                    >
                        {isFetching ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            'Continue'
                        )}
                    </Button>
                </CardContent>
            </Card>
        </Box>
    );
};

export const LoginPage = () => {
    return (
        <UnauthenticatedTemplate>
            <UnauthenticatedContent />
        </UnauthenticatedTemplate>
    );
};
